import { create } from 'zustand';

/**
 * Undo Store
 *
 * Manages undo/redo history for reversible actions.
 * Stores action snapshots that can be replayed backwards.
 *
 * Also tracks a single ephemeral "undo pill" state — UI surfaces a
 * dismissable Undo button next to the delete icon for `PILL_TIMEOUT_MS`
 * after a delete, then auto-fades. Only the most recent delete is shown,
 * even though the underlying stack keeps the full history.
 */

export type UndoableAction = {
  type: 'token:add' | 'token:remove' | 'token:move' | 'mob:add' | 'mob:remove' | 'bulk:remove';
  description: string;
  undo: () => void | Promise<void>;
  redo: () => void | Promise<void>;
};

interface UndoState {
  // History stacks
  undoStack: UndoableAction[];
  redoStack: UndoableAction[];

  // Max history size
  maxHistory: number;

  // Ephemeral "undo pill" — single slot, fades after PILL_TIMEOUT_MS.
  // pillActionId points into undoStack so we know exactly which action
  // the pill click should undo (always the most recent push for now,
  // but storing the id avoids races if more deletes land mid-pill).
  pillActionId: string | null;
  pillDescription: string | null;
  pillExpiresAt: number | null;

  // Actions
  push: (action: UndoableAction) => void;
  /** Push a delete-style action AND surface the undo pill for 30s. */
  pushDelete: (action: UndoableAction) => void;
  /** Undo the action behind the current pill (clears the pill). */
  undoPill: () => void;
  dismissPill: () => void;
  undo: () => void;
  redo: () => void;
  canUndo: () => boolean;
  canRedo: () => boolean;
  clear: () => void;
}

const MAX_HISTORY = 50;
const PILL_TIMEOUT_MS = 30_000;

// Module-scoped timer so a fresh delete cancels the previous fade.
let pillFadeTimer: ReturnType<typeof setTimeout> | null = null;

function clearPillTimer() {
  if (pillFadeTimer) {
    clearTimeout(pillFadeTimer);
    pillFadeTimer = null;
  }
}

// Each pushed action gets a stable id we can match the pill against
// (avoids relying on stack indices, which shift as undo/redo runs).
type ActionWithId = UndoableAction & { id: string };
function withId(action: UndoableAction): ActionWithId {
  return { ...action, id: `act-${Date.now()}-${Math.random().toString(36).slice(2, 8)}` };
}

export const useUndoStore = create<UndoState>((set, get) => ({
  undoStack: [],
  redoStack: [],
  maxHistory: MAX_HISTORY,

  pillActionId: null,
  pillDescription: null,
  pillExpiresAt: null,

  push: (action) => {
    const tagged = withId(action);
    set((state) => {
      const newStack = [...state.undoStack, tagged];
      if (newStack.length > state.maxHistory) {
        newStack.shift();
      }
      return {
        undoStack: newStack,
        redoStack: [], // Clear redo stack on new action
      };
    });
  },

  pushDelete: (action) => {
    const tagged = withId(action);
    set((state) => {
      const newStack = [...state.undoStack, tagged];
      if (newStack.length > state.maxHistory) {
        newStack.shift();
      }
      return {
        undoStack: newStack,
        redoStack: [],
        pillActionId: tagged.id,
        pillDescription: action.description,
        pillExpiresAt: Date.now() + PILL_TIMEOUT_MS,
      };
    });

    clearPillTimer();
    pillFadeTimer = setTimeout(() => {
      // Only clear if the pill still belongs to THIS action — another
      // delete may have replaced it before the timer fired.
      const cur = get().pillActionId;
      if (cur === tagged.id) {
        set({ pillActionId: null, pillDescription: null, pillExpiresAt: null });
      }
      pillFadeTimer = null;
    }, PILL_TIMEOUT_MS);
  },

  undoPill: () => {
    const { pillActionId, undoStack } = get();
    if (!pillActionId) return;

    // Find the action by id (don't assume top-of-stack — stack may have shifted)
    const idx = undoStack.findIndex((a) => (a as ActionWithId).id === pillActionId);
    if (idx === -1) {
      set({ pillActionId: null, pillDescription: null, pillExpiresAt: null });
      clearPillTimer();
      return;
    }

    const action = undoStack[idx];
    void Promise.resolve(action.undo()).catch((err) => {
      console.error('[undoStore] undoPill: action.undo() failed:', err);
    });

    const newStack = [...undoStack.slice(0, idx), ...undoStack.slice(idx + 1)];
    set((state) => ({
      undoStack: newStack,
      redoStack: [...state.redoStack, action],
      pillActionId: null,
      pillDescription: null,
      pillExpiresAt: null,
    }));

    clearPillTimer();
  },

  dismissPill: () => {
    set({ pillActionId: null, pillDescription: null, pillExpiresAt: null });
    clearPillTimer();
  },

  undo: () => {
    const { undoStack, redoStack } = get();
    if (undoStack.length === 0) return;

    const action = undoStack[undoStack.length - 1];
    void Promise.resolve(action.undo()).catch((err) => {
      console.error('[undoStore] undo: action.undo() failed:', err);
    });

    set({
      undoStack: undoStack.slice(0, -1),
      redoStack: [...redoStack, action],
    });
  },

  redo: () => {
    const { undoStack, redoStack } = get();
    if (redoStack.length === 0) return;

    const action = redoStack[redoStack.length - 1];
    void Promise.resolve(action.redo()).catch((err) => {
      console.error('[undoStore] redo: action.redo() failed:', err);
    });

    set({
      undoStack: [...undoStack, action],
      redoStack: redoStack.slice(0, -1),
    });
  },

  canUndo: () => get().undoStack.length > 0,
  canRedo: () => get().redoStack.length > 0,

  clear: () => {
    clearPillTimer();
    set({
      undoStack: [],
      redoStack: [],
      pillActionId: null,
      pillDescription: null,
      pillExpiresAt: null,
    });
  },
}));
